/**
 * WeeklyThemesPage — themed weekly publishing schedule manager.
 *
 * UX flow:
 *   1. Grid of saved themes (per-client) with quick-actions: Edit / Generate / Delete
 *   2. "+ New theme" opens the editor. Optional "Use a template" toggle
 *      pre-fills 7 days from a starter (Bluesky thought-leader, restaurant, etc.)
 *   3. Editor: theme name + client + defaults (audience/tone/platforms) + 7-day grid
 *   4. "Generate weeks" dialog: start date, # weeks (1–8), mode, optional day filter
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useClients } from '../hooks/useClients';
import { useFeature, FeatureLockedCard } from '../hooks/useFeatures';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  CalendarRange, Plus, Edit2, Trash2, Sparkles, Loader2, Wand2, Layers,
  CalendarPlus, FileText, ImageIcon, Send, Clock, Check, Bookmark, Globe2, User,
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const TONES = ['Professional', 'Educational', 'Motivational', 'Promotional', 'Casual', 'Friendly', 'Conversational', 'Reflective', 'Heartfelt'];
const PLATFORMS = ['Instagram', 'LinkedIn', 'Facebook', 'Bluesky', 'YouTube Shorts', 'TikTok'];

const emptyDay = () => ({ topic: '', tone: '', platforms: [], time: '09:00', style_profile_id: '' });
const emptyForm = () => ({
  id: null,
  name: '',
  client_id: '',
  default_audience: '',
  default_tone: 'Professional',
  default_platforms: ['Bluesky'],
  default_weeks: 4,
  style_profile_id: '',
  schedule: DAYS.reduce((acc, d) => ({ ...acc, [d]: emptyDay() }), {}),
});

// Duration presets shown in the Generate dialog. `weeks` is the source of
// truth; days are derived for display. 4 weeks (30 days) is the default —
// matching the most common "monthly content plan" workflow.
const DURATION_PRESETS = [
  { weeks: 1,  label: '1 week',   days: 7 },
  { weeks: 2,  label: '2 weeks',  days: 14 },
  { weeks: 4,  label: '30 days',  days: 28, hint: 'Most popular' },
  { weeks: 8,  label: '60 days',  days: 56 },
  { weeks: 13, label: '90 days',  days: 91 },
];
const MAX_WEEKS = 13;

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export const WeeklyThemesPage = () => {
  const { api, user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const { clients } = useClients();
  const themeFeature = useFeature('weekly_themes');
  const [themes, setThemes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [styleProfiles, setStyleProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} = closed/new
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [generateFor, setGenerateFor] = useState(null);
  // Template management state — `saveTemplateFor` opens the "save theme as
  // template" dialog; `editingTemplate` opens the standalone template editor.
  const [saveTemplateFor, setSaveTemplateFor] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null); // {} = new, {...} = edit

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, tpl, sp] = await Promise.allSettled([
        api.get('/weekly-themes'),
        api.get('/weekly-themes/templates'),
        api.get('/style-profiles'),
      ]);
      if (t.status === 'fulfilled') setThemes(t.value.data?.themes || []);
      else toast.error('Failed to load themes');
      if (tpl.status === 'fulfilled') setTemplates(tpl.value.data?.templates || []);
      if (sp.status === 'fulfilled') setStyleProfiles(sp.value.data?.profiles || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (themeFeature.loading || themeFeature.locked) {
      setLoading(false);
      return;
    }
    refresh();
  }, [themeFeature.loading, themeFeature.locked]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Open editor (create or edit) ---
  const openCreate = () => { setForm(emptyForm()); setEditing({}); };
  // Open the editor with a template pre-applied — used by the gallery cards.
  const openCreateWithTemplate = (tpl) => {
    const next = emptyForm();
    next.name = tpl.name;
    next.default_audience = tpl.default_audience;
    next.default_tone = tpl.default_tone;
    next.default_platforms = tpl.default_platforms || ['Bluesky'];
    next.schedule = DAYS.reduce((acc, d) => ({
      ...acc,
      [d]: {
        topic: tpl.schedule[d]?.topic || '',
        tone: tpl.schedule[d]?.tone || '',
        platforms: tpl.schedule[d]?.platforms || [],
        time: tpl.schedule[d]?.time || '09:00',
        style_profile_id: '',
      },
    }), {});
    setForm(next);
    setEditing({});
  };
  const openEdit = (t) => {
    setForm({
      id: t.id,
      name: t.name,
      client_id: t.client_id || '',
      default_audience: t.default_audience,
      default_tone: t.default_tone || 'Professional',
      default_platforms: t.default_platforms || ['Bluesky'],
      default_weeks: t.default_weeks || 4,
      style_profile_id: t.style_profile_id || '',
      schedule: DAYS.reduce((acc, d) => ({
        ...acc,
        [d]: {
          topic: t.schedule?.[d]?.topic || '',
          tone: t.schedule?.[d]?.tone || '',
          platforms: t.schedule?.[d]?.platforms || [],
          time: t.schedule?.[d]?.time || '09:00',
          style_profile_id: t.schedule?.[d]?.style_profile_id || '',
        },
      }), {}),
    });
    setEditing(t);
  };

  const applyTemplate = (tplId) => {
    const tpl = templates.find((x) => x.id === tplId);
    if (!tpl) return;
    setForm((f) => ({
      ...f,
      name: f.name || tpl.name,
      default_audience: tpl.default_audience,
      default_tone: tpl.default_tone,
      default_platforms: tpl.default_platforms,
      schedule: DAYS.reduce((acc, d) => ({
        ...acc,
        [d]: {
          topic: tpl.schedule[d]?.topic || '',
          tone: tpl.schedule[d]?.tone || '',
          platforms: tpl.schedule[d]?.platforms || [],
          time: tpl.schedule[d]?.time || '09:00',
        },
      }), {}),
    }));
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const togglePlatform = (id) => {
    setForm((f) => ({
      ...f,
      default_platforms: f.default_platforms.includes(id)
        ? f.default_platforms.filter((p) => p !== id)
        : [...f.default_platforms, id],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.default_audience.trim()) return toast.error('Default audience is required');
    if (form.default_platforms.length === 0) return toast.error('Pick at least one default platform');
    const missing = DAYS.filter((d) => !form.schedule[d].topic.trim());
    if (missing.length) return toast.error(`Topic missing for: ${missing.map((d) => DAY_SHORT[d]).join(', ')}`);

    const payload = {
      name: form.name.trim(),
      client_id: form.client_id || null,
      default_audience: form.default_audience.trim(),
      default_tone: form.default_tone,
      default_platforms: form.default_platforms,
      default_weeks: Math.max(1, Math.min(MAX_WEEKS, Number(form.default_weeks) || 4)),
      // `""` is the explicit-clear sentinel on update (matches Clients pattern);
      // on create the backend treats falsy as "no profile" — same effect.
      style_profile_id: form.style_profile_id || '',
      schedule: DAYS.reduce((acc, d) => {
        const s = form.schedule[d];
        acc[d] = {
          topic: s.topic.trim(),
          tone: s.tone || null,
          platforms: s.platforms?.length ? s.platforms : null,
          time: s.time || null,
          style_profile_id: s.style_profile_id || null,
        };
        return acc;
      }, {}),
    };
    setSubmitting(true);
    try {
      if (form.id) {
        await api.put(`/weekly-themes/${form.id}`, payload);
        toast.success('Theme updated');
      } else {
        await api.post('/weekly-themes', payload);
        toast.success('Theme created');
      }
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete theme "${t.name}"? Posts already generated keep working.`)) return;
    try {
      await api.delete(`/weekly-themes/${t.id}`);
      toast.success('Theme archived');
      await refresh();
    } catch (_) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    const label = tpl.scope === 'global' ? `the global template "${tpl.name}"` : `"${tpl.name}"`;
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await api.delete(`/weekly-themes/templates/${tpl.id}`);
      toast.success('Template deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="weekly-themes-page">
      {themeFeature.locked && (
        <FeatureLockedCard
          featureName={themeFeature.featureName || 'Weekly Themes'}
          requiredTiers={themeFeature.requiredTiers}
          description={themeFeature.description}
          testId="weekly-themes-locked"
        />
      )}
      {!themeFeature.locked && (
      <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <CalendarRange className="w-7 h-7 text-indigo-600" />
            Weekly Themes
          </h1>
          <p className="text-zinc-500">
            Define a 7-day topic rotation per client. The pattern repeats — generate up to <b>13 weeks (~90 days)</b> of pre-scheduled posts in one click.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-theme-btn">
          <Plus className="w-4 h-4 mr-2" /> New theme
        </Button>
      </div>

      {/* Starter templates gallery — global templates (admin-curated) + the
          user's personal saved templates. Each card "applies" the template
          into a new theme; edit/delete actions appear when the user owns it
          (admins can manage globals too). */}
      {templates.length > 0 && (
        <details className="group bg-indigo-50/40 border border-indigo-100 rounded-lg p-3" open={themes.length === 0}>
          <summary className="cursor-pointer text-sm font-medium text-indigo-900 flex items-center gap-2 select-none">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            Starter templates ({templates.length})
            <span className="ml-2 text-[10px] text-indigo-700/60">
              {templates.filter((t) => t.scope === 'global').length} global · {templates.filter((t) => t.scope !== 'global').length} yours
            </span>
            <span className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.preventDefault(); setEditingTemplate({}); }}
                data-testid="new-template-btn"
                className="h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" /> New template
              </Button>
              <span className="text-xs text-indigo-700/60 group-open:hidden">click to expand</span>
            </span>
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {templates.map((tpl) => {
              const isGlobal = tpl.scope === 'global';
              const canEdit = tpl.can_edit;
              return (
                <div
                  key={tpl.id}
                  className="group/tpl relative text-left bg-white border border-zinc-200 hover:border-indigo-400 hover:shadow-sm rounded-md p-3 transition-all"
                  data-testid={`starter-template-${tpl.id}`}
                >
                  <button
                    type="button"
                    onClick={() => openCreateWithTemplate(tpl)}
                    className="w-full text-left"
                    data-testid={`apply-template-card-${tpl.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-sm text-zinc-900 line-clamp-1 flex-1">{tpl.name}</p>
                      <Badge
                        variant={isGlobal ? 'default' : 'secondary'}
                        className={`text-[9px] shrink-0 ${isGlobal ? 'bg-indigo-600 hover:bg-indigo-600' : ''}`}
                      >
                        {isGlobal ? <><Globe2 className="w-2.5 h-2.5 mr-0.5" /> Global</> : <><User className="w-2.5 h-2.5 mr-0.5" /> Yours</>}
                      </Badge>
                    </div>
                    {tpl.description && (
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{tpl.description}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(tpl.default_platforms || []).slice(0, 4).map((p) => (
                        <Badge key={p} variant="secondary" className="text-[10px]">{p}</Badge>
                      ))}
                      <Badge variant="outline" className="text-[10px]">{tpl.default_tone}</Badge>
                      {tpl.default_weeks && (
                        <Badge variant="outline" className="text-[10px]">{tpl.default_weeks * 7}d</Badge>
                      )}
                    </div>
                  </button>
                  {canEdit && (
                    <div className="absolute top-2 right-2 flex gap-0.5 opacity-0 group-hover/tpl:opacity-100 transition-opacity">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); setEditingTemplate(tpl); }}
                        data-testid={`edit-template-${tpl.id}`}
                        title="Edit template"
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 text-rose-600 hover:bg-rose-50"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(tpl); }}
                        data-testid={`delete-template-${tpl.id}`}
                        title="Delete template"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : themes.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <CalendarRange className="w-10 h-10 mx-auto text-zinc-300" />
            <p className="font-medium">No themes yet</p>
            <p className="text-sm text-zinc-500">
              Click a starter template above to clone, or build one from scratch.
            </p>
            <Button onClick={openCreate} className="mt-2" data-testid="new-theme-empty-btn">
              <Plus className="w-4 h-4 mr-2" /> Build from scratch
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {themes.map((t) => (
            <Card key={t.id} className="border-zinc-200" data-testid={`theme-card-${t.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="truncate">{t.name}</span>
                  {t.client_name && <Badge variant="secondary" className="shrink-0">{t.client_name}</Badge>}
                </CardTitle>
                <CardDescription className="text-xs">
                  {(t.default_platforms || []).join(' · ')} · tone: {t.default_tone}
                  {t.style_profile_id && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-indigo-600">
                      <Sparkles className="w-3 h-3" />
                      voice
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <ul className="text-xs space-y-1">
                  {DAYS.map((d) => (
                    <li key={d} className="flex gap-2">
                      <span className="font-mono text-zinc-400 w-8 shrink-0">{DAY_SHORT[d]}</span>
                      <span className="truncate text-zinc-700">{t.schedule?.[d]?.topic || '—'}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" onClick={() => setGenerateFor(t)} data-testid={`generate-btn-${t.id}`}>
                    <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Generate
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`edit-btn-${t.id}`}>
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSaveTemplateFor(t)}
                    data-testid={`save-as-template-btn-${t.id}`}
                    title="Save as reusable template"
                  >
                    <Bookmark className="w-3.5 h-3.5 mr-1.5" /> Save as template
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(t)}
                    className="text-rose-600 hover:bg-rose-50 ml-auto"
                    data-testid={`delete-btn-${t.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {t.last_generated_at && (
                  <p className="text-[11px] text-zinc-400">
                    Last generated {new Date(t.last_generated_at).toLocaleDateString()} · {t.last_generated_count || 0} posts
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor */}
      {editing && (
        <ThemeEditor
          form={form}
          setForm={setForm}
          clients={clients}
          templates={templates}
          styleProfiles={styleProfiles}
          applyTemplate={applyTemplate}
          togglePlatform={togglePlatform}
          onCancel={() => setEditing(null)}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}

      {/* Generate */}
      {generateFor && (
        <GenerateDialog
          api={api}
          theme={generateFor}
          onClose={() => { setGenerateFor(null); refresh(); }}
        />
      )}

      {/* Save existing theme as a reusable template */}
      {saveTemplateFor && (
        <SaveAsTemplateDialog
          api={api}
          theme={saveTemplateFor}
          isAdmin={isAdmin}
          onClose={() => setSaveTemplateFor(null)}
          onSaved={() => { setSaveTemplateFor(null); refresh(); }}
        />
      )}

      {/* Create / edit a template directly */}
      {editingTemplate && (
        <TemplateEditor
          api={api}
          template={editingTemplate}
          isAdmin={isAdmin}
          onClose={() => setEditingTemplate(null)}
          onSaved={() => { setEditingTemplate(null); refresh(); }}
        />
      )}
      </>
      )}
    </div>
  );
};

// =====================================================
// Editor (theme + 7-day grid)
// =====================================================
const ThemeEditor = ({ form, setForm, clients, templates, styleProfiles, applyTemplate, togglePlatform, onCancel, onSubmit, submitting }) => {
  const { api } = useAuth();
  const [templateId, setTemplateId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewDay, setPreviewDay] = useState('monday');
  const [savingPreview, setSavingPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null); // base64 data url
  const [genImage, setGenImage] = useState(false);

  // Resolve which style profile and day-cfg the preview should use — defaults
  // to Monday and respects per-day override > theme-level fallback.
  const previewActiveDay = form.schedule[previewDay] || form.schedule.monday;
  const previewStyleId = previewActiveDay?.style_profile_id || form.style_profile_id || '';
  const previewStyleName = previewStyleId
    ? (styleProfiles.find((p) => p.id === previewStyleId)?.name || 'Unknown profile')
    : null;

  const handlePreview = async () => {
    if (!previewActiveDay?.topic?.trim()) {
      toast.error('Add a topic to that day first');
      return;
    }
    if (!form.default_audience.trim()) {
      toast.error('Default audience is required');
      return;
    }
    setPreviewing(true);
    setPreviewResult(null);
    setPreviewImage(null);
    try {
      const r = await api.post('/weekly-themes/preview-voice', {
        topic: previewActiveDay.topic.trim(),
        tone: previewActiveDay.tone || form.default_tone,
        audience: form.default_audience.trim(),
        platform: (previewActiveDay.platforms?.[0]) || form.default_platforms[0] || 'Bluesky',
        style_profile_id: previewStyleId || null,
      });
      setPreviewResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSavePreview = async () => {
    if (!previewResult) return;
    setSavingPreview(true);
    try {
      await api.post('/weekly-themes/preview-voice/save', {
        topic: previewResult.topic,
        tone: previewResult.tone,
        audience: form.default_audience.trim(),
        platform: previewResult.platform,
        post_title: previewResult.post_title || '',
        caption: previewResult.caption,
        hashtags: previewResult.hashtags || '',
        image_prompt: previewResult.image_prompt || '',
        style_profile_id: previewResult.style_profile_id || null,
        client_id: form.client_id || null,
      });
      toast.success('Saved as draft in My Posts');
      // Mark the preview as "saved" so the button can swap to a confirmation state
      setPreviewResult((r) => ({ ...r, _saved: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save draft');
    } finally {
      setSavingPreview(false);
    }
  };

  // Generate a throwaway sample image from the preview's image_prompt — does
  // not save or consume the image quota. Mirrors the AI Post Manager preview.
  const handleGenPreviewImage = async () => {
    const prompt = (previewResult?.image_prompt || previewResult?.topic || '').trim();
    if (!prompt) {
      toast.error('No image prompt available for this preview');
      return;
    }
    setGenImage(true);
    setPreviewImage(null);
    try {
      const r = await api.post('/weekly-themes/preview-image', {
        image_prompt: prompt,
        image_model: 'nano-banana',
      });
      if (r.data?.image_b64) {
        setPreviewImage(`data:image/png;base64,${r.data.image_b64}`);
      } else {
        toast.error('Image generation returned nothing');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Image generation failed');
    } finally {
      setGenImage(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="theme-editor-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? 'Edit theme' : 'New weekly theme'}</DialogTitle>
          <DialogDescription>
            Define a topic rotation. Each day can override the defaults below.
          </DialogDescription>
        </DialogHeader>

        {/* Starter template picker — always visible so users can re-prefill
            an existing theme too. Descriptions surface what each template is. */}
        {templates.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 space-y-2">
            <Label className="text-xs font-semibold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              Start from a template
            </Label>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[200px]">
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger data-testid="theme-template-select">
                    <SelectValue placeholder="— Pick a starter —" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!templateId}
                onClick={() => applyTemplate(templateId)}
                data-testid="apply-template-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Apply
              </Button>
            </div>
            {templateId && (
              <p className="text-xs text-indigo-800/80">
                {templates.find((t) => t.id === templateId)?.description}
              </p>
            )}
            {form.id && (
              <p className="text-[11px] text-indigo-700/70">
                ⚠ Applying a template overwrites your current 7-day schedule + defaults.
              </p>
            )}
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Theme name <span className="text-rose-500">*</span></Label>
              <Input
                placeholder="e.g. Bluesky thought-leadership"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="theme-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Client (optional)</Label>
              <Select value={form.client_id || '__none__'} onValueChange={(v) => setForm({ ...form, client_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="theme-client-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No client —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Default audience <span className="text-rose-500">*</span></Label>
            <Input
              placeholder="e.g. small business owners on a tight marketing budget"
              value={form.default_audience}
              onChange={(e) => setForm({ ...form, default_audience: e.target.value })}
              data-testid="theme-audience-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Default tone</Label>
              <Select value={form.default_tone} onValueChange={(v) => setForm({ ...form, default_tone: v })}>
                <SelectTrigger data-testid="theme-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                Style profile (optional)
              </Label>
              <Select
                value={form.style_profile_id || '__none__'}
                onValueChange={(v) => setForm({ ...form, style_profile_id: v === '__none__' ? '' : v })}
              >
                <SelectTrigger data-testid="theme-style-profile-select">
                  <SelectValue placeholder="— No style profile —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No style profile —</SelectItem>
                  {styleProfiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500">
                Applies your saved brand voice + rules to every post. Each day can override.
              </p>
            </div>
          </div>
          <div>
            <Label>Default platforms <span className="text-rose-500">*</span></Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {PLATFORMS.map((p) => {
                const active = form.default_platforms.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlatform(p)}
                    className={`p-1.5 rounded text-xs border ${active ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-zinc-200 text-zinc-600'}`}
                    data-testid={`default-platform-${p.toLowerCase().replace(/[^a-z]/g, '')}`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 7-day grid */}
          <div className="space-y-2 border-t border-zinc-200 pt-3">
            <Label className="text-sm font-semibold">Weekly pattern</Label>
            <p className="text-[11px] text-zinc-500 -mt-1">
              Set each weekday's topic once. This pattern repeats across every week of your run.
            </p>

            {/* Duration — how many weeks the pattern should repeat for when
                you click Generate. Stored on the theme so it's the default
                next time, but you can still override at generation time. */}
            <div className="bg-indigo-50/40 border border-indigo-100 rounded-md p-3 space-y-2 my-2">
              <Label className="text-xs font-semibold flex items-center gap-1.5">
                <CalendarRange className="w-3.5 h-3.5 text-indigo-600" />
                Run length
              </Label>
              <div className="grid grid-cols-5 gap-1.5">
                {DURATION_PRESETS.map((p) => {
                  const active = Number(form.default_weeks) === p.weeks;
                  return (
                    <button
                      key={p.weeks}
                      type="button"
                      onClick={() => setForm({ ...form, default_weeks: p.weeks })}
                      className={`relative px-2 py-2 rounded text-xs border transition-colors ${active ? 'border-indigo-500 bg-white text-indigo-900 font-semibold' : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'}`}
                      data-testid={`theme-duration-${p.weeks}w`}
                    >
                      {p.label}
                      {p.hint && (
                        <span className="absolute -top-1.5 -right-1.5 text-[9px] bg-indigo-600 text-white px-1 py-0.5 rounded-full leading-none">★</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-[11px] text-zinc-500 whitespace-nowrap">or custom (1–{MAX_WEEKS}):</Label>
                <Input
                  type="number"
                  min={1}
                  max={MAX_WEEKS}
                  value={form.default_weeks}
                  onChange={(e) => setForm({ ...form, default_weeks: Math.max(1, Math.min(MAX_WEEKS, Number(e.target.value) || 1)) })}
                  className="h-7 text-xs w-20"
                  data-testid="theme-duration-custom"
                />
                <span className="text-[11px] text-zinc-500">
                  weeks ≈ <b>{form.default_weeks * 7} days</b>
                </span>
              </div>
              <p className="text-[10px] text-zinc-500">
                You can change this when you click Generate. Default = {form.default_weeks} week{form.default_weeks === 1 ? '' : 's'} (~{form.default_weeks * 7} days).
              </p>
            </div>
            <div className="space-y-2">
              {DAYS.map((d) => {
                const day = form.schedule[d];
                const dayStyle = day.style_profile_id || form.style_profile_id || '';
                const dayStyleName = dayStyle
                  ? (styleProfiles.find((p) => p.id === dayStyle)?.name || '')
                  : '';
                return (
                  <div key={d} className="bg-zinc-50 rounded-md p-2 space-y-1.5">
                    {/* Day row: stacks on mobile, collapses to 12-col grid on sm+ */}
                    <div className="space-y-1.5 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center">
                      <div className="flex items-center gap-2 sm:contents">
                        <div className="sm:col-span-1 font-mono text-xs text-zinc-600 font-semibold w-8 shrink-0">{DAY_SHORT[d]}</div>
                        <Input
                          className="flex-1 sm:col-span-6 h-8 text-sm"
                          placeholder="Topic for this day (required)"
                          value={day.topic}
                          onChange={(e) => setForm({
                            ...form,
                            schedule: { ...form.schedule, [d]: { ...day, topic: e.target.value } },
                          })}
                          data-testid={`day-topic-${d}`}
                        />
                      </div>
                      <div className="grid grid-cols-5 gap-2 sm:contents">
                        <Select
                          value={day.tone || '__default__'}
                          onValueChange={(v) => setForm({
                            ...form,
                            schedule: { ...form.schedule, [d]: { ...day, tone: v === '__default__' ? '' : v } },
                          })}
                        >
                          <SelectTrigger className="col-span-3 sm:col-span-3 h-8 text-xs" data-testid={`day-tone-${d}`}>
                            <SelectValue placeholder="Tone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">↳ Default ({form.default_tone})</SelectItem>
                            {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          type="time"
                          className="col-span-2 sm:col-span-2 h-8 text-xs"
                          value={day.time || '09:00'}
                          onChange={(e) => setForm({
                            ...form,
                            schedule: { ...form.schedule, [d]: { ...day, time: e.target.value } },
                          })}
                          data-testid={`day-time-${d}`}
                        />
                      </div>
                    </div>
                    {/* Per-day style override — only show when user has any profiles
                        OR the theme has one (so they can override on this day only) */}
                    {(styleProfiles.length > 0 || form.style_profile_id) && (
                      <div className="flex items-center gap-2 sm:grid sm:grid-cols-12 sm:gap-2">
                        <div className="hidden sm:block sm:col-span-1" />
                        <Label className="sm:col-span-2 text-[10px] text-zinc-500 flex items-center gap-1 shrink-0">
                          <Sparkles className="w-3 h-3 text-indigo-500" /> Voice
                        </Label>
                        <Select
                          value={day.style_profile_id || '__default__'}
                          onValueChange={(v) => setForm({
                            ...form,
                            schedule: {
                              ...form.schedule,
                              [d]: { ...day, style_profile_id: v === '__default__' ? '' : v },
                            },
                          })}
                        >
                          <SelectTrigger
                            className="flex-1 sm:col-span-9 h-7 text-xs"
                            data-testid={`day-style-${d}`}
                          >
                            <SelectValue placeholder="↳ Use default" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">
                              ↳ Use default{dayStyleName && !day.style_profile_id ? ` (${dayStyleName})` : ''}
                            </SelectItem>
                            {styleProfiles.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-500">
              Time is in your local browser timezone. Posts will publish at this time on the matching day each week.
            </p>
          </div>

          {/* Preview voice — generates one sample post in the chosen voice
              without saving or consuming quota. */}
          <div className="border-t border-zinc-200 pt-3 space-y-2 bg-indigo-50/40 -mx-1 px-3 py-3 rounded-md">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <Label className="flex items-center gap-1 text-sm font-semibold">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                  Preview voice
                </Label>
                <p className="text-[11px] text-zinc-500">
                  Generate one sample caption to dial in the tone before committing the full run. Doesn't count against your quota.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={previewDay} onValueChange={setPreviewDay}>
                  <SelectTrigger className="h-8 text-xs w-28" data-testid="preview-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => (
                      <SelectItem key={d} value={d}>{DAY_SHORT[d]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handlePreview}
                  disabled={previewing}
                  data-testid="preview-voice-btn"
                >
                  {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                  Preview
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">
              Topic: <b>{previewActiveDay?.topic || '(empty)'}</b>
              {' · '}
              Voice: <b>{previewStyleName || 'Default (no profile)'}</b>
            </p>
            {previewResult && (
              <div className="bg-white border border-indigo-200 rounded-md p-3 space-y-2" data-testid="preview-result">
                <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                  <Badge variant="outline">{previewResult.platform}</Badge>
                  <span>{previewResult.tone}</span>
                  {previewResult.style_profile_name && (
                    <span className="inline-flex items-center gap-0.5 text-indigo-600">
                      <Sparkles className="w-3 h-3" />
                      {previewResult.style_profile_name}
                    </span>
                  )}
                </div>
                {previewResult.post_title && (
                  <p className="font-semibold text-sm text-zinc-900">{previewResult.post_title}</p>
                )}
                <p className="text-sm text-zinc-800 whitespace-pre-wrap">{previewResult.caption}</p>
                {previewResult.hashtags && (
                  <p className="text-xs text-indigo-600">{previewResult.hashtags}</p>
                )}
                {/* Sample image — throwaway preview, doesn't touch the image quota */}
                {previewImage && (
                  <img
                    src={previewImage}
                    alt="Sample"
                    className="rounded-md border border-indigo-100 w-full max-h-72 object-cover"
                    data-testid="preview-sample-image"
                  />
                )}
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-100">
                  <Button
                    type="button"
                    size="sm"
                    variant={previewResult._saved ? 'outline' : 'default'}
                    onClick={handleSavePreview}
                    disabled={savingPreview || previewResult._saved}
                    data-testid="save-preview-btn"
                  >
                    {savingPreview ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : previewResult._saved ? (
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                    ) : (
                      <FileText className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {previewResult._saved ? 'Saved to My Posts' : 'Save as draft'}
                  </Button>
                  {(previewResult.image_prompt || previewResult.topic) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={handleGenPreviewImage}
                      disabled={genImage}
                      data-testid="preview-sample-image-btn"
                    >
                      {genImage ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {previewImage ? 'Regenerate image' : 'Generate sample image'}
                    </Button>
                  )}
                  <p className="text-[11px] text-zinc-400">
                    Free preview — sample image doesn't count toward your quota.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button onClick={onSubmit} disabled={submitting} data-testid="save-theme-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {form.id ? 'Save changes' : 'Create theme'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================
// Generate dialog
// =====================================================
const GenerateDialog = ({ api, theme, onClose }) => {
  const [startDate, setStartDate] = useState(tomorrow());
  // Prefill weeks from the theme's saved default (falls back to 4 for older
  // themes created before the duration selector existed).
  const [weeks, setWeeks] = useState(theme.default_weeks || 4);
  const [mode, setMode] = useState('drafts');
  const [onlyDays, setOnlyDays] = useState(new Set(DAYS));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const totalDays = useMemo(() => weeks * onlyDays.size, [weeks, onlyDays]);
  const expectedCount = useMemo(() => {
    const platforms = (theme.default_platforms || []).length || 1;
    return totalDays * platforms;
  }, [totalDays, theme.default_platforms]);

  // Computed end date — gives users a real calendar feel of the run length.
  const endDateLabel = useMemo(() => {
    if (!startDate) return null;
    try {
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(start);
      end.setDate(end.getDate() + weeks * 7 - 1);
      return end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return null; }
  }, [startDate, weeks]);

  const toggleDay = (d) => {
    const next = new Set(onlyDays);
    if (next.has(d)) next.delete(d); else next.add(d);
    setOnlyDays(next);
  };

  const submit = async () => {
    if (onlyDays.size === 0) return toast.error('Select at least one day');
    setSubmitting(true);
    try {
      const r = await api.post(`/weekly-themes/${theme.id}/generate`, {
        start_date: startDate,
        weeks,
        mode,
        only_days: Array.from(onlyDays),
      });
      setResult(r.data);
      toast.success(`Generated ${r.data.count} posts`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="generate-dialog">
        <DialogHeader>
          <DialogTitle>Generate from "{theme.name}"</DialogTitle>
          <DialogDescription>
            Posts are pre-scheduled to each day's slot and saved to My Posts.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Start date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="generate-start-date"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Duration</Label>
              <div className="grid grid-cols-5 gap-1.5">
                {DURATION_PRESETS.map((p) => {
                  const active = weeks === p.weeks;
                  return (
                    <button
                      key={p.weeks}
                      type="button"
                      onClick={() => setWeeks(p.weeks)}
                      className={`relative px-2 py-2 rounded text-xs border transition-colors ${active ? 'border-indigo-500 bg-indigo-50 text-indigo-900 font-semibold' : 'border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
                      data-testid={`duration-preset-${p.weeks}w`}
                    >
                      {p.label}
                      {p.hint && (
                        <span className="absolute -top-1.5 -right-1.5 text-[9px] bg-indigo-600 text-white px-1 py-0.5 rounded-full leading-none">
                          ★
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Label className="text-[11px] text-zinc-500 whitespace-nowrap">or custom (1–{MAX_WEEKS}):</Label>
                <Input
                  type="number"
                  min={1}
                  max={MAX_WEEKS}
                  value={weeks}
                  onChange={(e) => setWeeks(Math.max(1, Math.min(MAX_WEEKS, Number(e.target.value) || 1)))}
                  className="h-7 text-xs w-20"
                  data-testid="generate-weeks"
                />
                <span className="text-[11px] text-zinc-500">weeks ≈ {weeks * 7} days</span>
              </div>
              {endDateLabel && (
                <p className="text-[11px] text-zinc-500 pt-1">
                  Runs through <b>{endDateLabel}</b>.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Days to include</Label>
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map((d) => {
                  const active = onlyDays.has(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(d)}
                      className={`px-1.5 py-1.5 rounded text-xs ${active ? 'bg-indigo-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
                      data-testid={`day-toggle-${d}`}
                    >
                      {DAY_SHORT[d]}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Mode</Label>
              <div className="grid grid-cols-1 gap-2">
                <ModeOption v="drafts" cur={mode} set={setMode} icon={FileText} title="Drafts only" desc="Save as scheduled drafts. No image cost." />
                <ModeOption v="drafts_with_images" cur={mode} set={setMode} icon={ImageIcon} title="Drafts + AI images" desc="Auto-generates one AI image per post in the background (Nano Banana · counts toward image quota)." />
                <ModeOption v="auto_publish" cur={mode} set={setMode} icon={Send} title="Auto-publish at scheduled time" desc="Posts go live automatically at each day's slot." />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              <b>Estimate:</b> ~{expectedCount} posts across {totalDays} posting days
              ({weeks} weeks × {onlyDays.size} active weekdays × {(theme.default_platforms || []).length || 1} platform{((theme.default_platforms || []).length || 1) === 1 ? '' : 's'}).
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="generate-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
              <div className="font-semibold text-emerald-900 flex items-center gap-2">
                <CalendarPlus className="w-4 h-4" /> {result.count} posts scheduled
              </div>
              <p className="text-emerald-800 text-xs mt-1">
                {result.first_scheduled ? `From ${new Date(result.first_scheduled).toLocaleString()}` : ''}
                {result.last_scheduled ? ` → ${new Date(result.last_scheduled).toLocaleString()}` : ''}
              </p>
              {result.failures > 0 && (
                <p className="text-amber-700 text-xs mt-1">⚠ {result.failures} skipped due to AI errors</p>
              )}
              {result.image_generation === 'queued' && (
                <p className="text-indigo-700 text-xs mt-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI images are generating in the background — they'll appear on each post shortly.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="generate-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                Generate {expectedCount} posts
              </Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="generate-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ModeOption = ({ v, cur, set, icon: Icon, title, desc }) => {
  const active = cur === v;
  return (
    <button
      type="button"
      onClick={() => set(v)}
      className={`p-2 rounded text-left border ${active ? 'border-indigo-500 bg-indigo-50' : 'border-zinc-200'}`}
      data-testid={`mode-option-${v}`}
    >
      <div className="flex items-center gap-2 mb-0.5">
        <Icon className={`w-3.5 h-3.5 ${active ? 'text-indigo-600' : 'text-zinc-500'}`} />
        <span className="font-medium text-sm">{title}</span>
      </div>
      <p className="text-xs text-zinc-500">{desc}</p>
    </button>
  );
};

// =====================================================
// Save-as-template — minimal dialog: name + description + scope (admins only)
// =====================================================
const SaveAsTemplateDialog = ({ api, theme, isAdmin, onClose, onSaved }) => {
  const [name, setName] = useState(theme.name || '');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('personal');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.post(`/weekly-themes/${theme.id}/save-as-template`, {
        name: name.trim(),
        description: description.trim(),
        scope: isAdmin ? scope : 'personal',
      });
      toast.success(scope === 'global' ? 'Saved as global template' : 'Saved as your template');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="save-as-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-indigo-600" /> Save as template
          </DialogTitle>
          <DialogDescription>
            Clones "{theme.name}"'s schedule, audience, tone, and platforms into a reusable template.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Template name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Bluesky Weekly Mix"
              data-testid="save-template-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When to use this template…"
              data-testid="save-template-desc-input"
            />
          </div>
          {isAdmin && (
            <div className="space-y-1 bg-indigo-50 border border-indigo-200 rounded p-2">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <Globe2 className="w-3.5 h-3.5 text-indigo-600" /> Visibility (admin)
              </Label>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setScope('personal')}
                  className={`p-1.5 text-xs rounded border ${scope === 'personal' ? 'border-indigo-500 bg-white text-indigo-900 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
                  data-testid="scope-personal-btn"
                >
                  <User className="w-3 h-3 mr-1 inline" /> Just for me
                </button>
                <button
                  type="button"
                  onClick={() => setScope('global')}
                  className={`p-1.5 text-xs rounded border ${scope === 'global' ? 'border-indigo-500 bg-white text-indigo-900 font-semibold' : 'border-zinc-200 bg-white text-zinc-600'}`}
                  data-testid="scope-global-btn"
                >
                  <Globe2 className="w-3 h-3 mr-1 inline" /> Global (all users)
                </button>
              </div>
              <p className="text-[10px] text-indigo-800/70">
                Global templates appear in every user's starter gallery.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-template-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bookmark className="w-4 h-4 mr-2" />}
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================================================
// TemplateEditor — full create/edit for a template (Mon-Sun grid + defaults)
// `template = {}` means new; otherwise it's an existing row to edit.
// =====================================================
const TemplateEditor = ({ api, template, isAdmin, onClose, onSaved }) => {
  const isNew = !template?.id;
  const [form, setForm] = useState(() => ({
    name: template?.name || '',
    description: template?.description || '',
    default_audience: template?.default_audience || '',
    default_tone: template?.default_tone || 'Professional',
    default_platforms: template?.default_platforms || ['Bluesky'],
    default_weeks: template?.default_weeks || 4,
    scope: template?.scope || 'personal',
    schedule: DAYS.reduce((acc, d) => ({
      ...acc,
      [d]: {
        topic: template?.schedule?.[d]?.topic || '',
        tone: template?.schedule?.[d]?.tone || '',
        platforms: template?.schedule?.[d]?.platforms || [],
        time: template?.schedule?.[d]?.time || '09:00',
      },
    }), {}),
  }));
  const [saving, setSaving] = useState(false);

  const togglePlatform = (p) => {
    setForm((f) => ({
      ...f,
      default_platforms: f.default_platforms.includes(p)
        ? f.default_platforms.filter((x) => x !== p)
        : [...f.default_platforms, p],
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.default_audience.trim()) { toast.error('Default audience is required'); return; }
    if (form.default_platforms.length === 0) { toast.error('Pick at least one platform'); return; }
    const missing = DAYS.filter((d) => !form.schedule[d].topic.trim());
    if (missing.length) { toast.error(`Topic missing for: ${missing.map((d) => DAY_SHORT[d]).join(', ')}`); return; }

    const scope = isAdmin ? form.scope : 'personal';
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      default_audience: form.default_audience.trim(),
      default_tone: form.default_tone,
      default_platforms: form.default_platforms,
      default_weeks: Math.max(1, Math.min(MAX_WEEKS, Number(form.default_weeks) || 4)),
      scope,
      schedule: DAYS.reduce((acc, d) => {
        const s = form.schedule[d];
        acc[d] = {
          topic: s.topic.trim(),
          tone: s.tone || null,
          platforms: s.platforms?.length ? s.platforms : null,
          time: s.time || null,
          style_profile_id: null,
        };
        return acc;
      }, {}),
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/weekly-themes/templates', payload);
        toast.success('Template created');
      } else {
        await api.put(`/weekly-themes/templates/${template.id}`, payload);
        toast.success('Template updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-indigo-600" />
            {isNew ? 'New template' : `Edit template: ${template.name}`}
          </DialogTitle>
          <DialogDescription>
            Templates pre-fill the New-theme dialog with their schedule + defaults. {isAdmin ? 'You can publish this globally for all users.' : 'Your personal templates only show in your own gallery.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Template name <span className="text-rose-500">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Real Estate — Weekly"
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Default tone</Label>
              <Select value={form.default_tone} onValueChange={(v) => setForm({ ...form, default_tone: v })}>
                <SelectTrigger data-testid="template-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Description (shown in the gallery card)</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="One-line summary of when to use this"
              data-testid="template-desc-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Default audience <span className="text-rose-500">*</span></Label>
            <Input
              value={form.default_audience}
              onChange={(e) => setForm({ ...form, default_audience: e.target.value })}
              placeholder="e.g. first-time home buyers in the metro area"
              data-testid="template-audience-input"
            />
          </div>
          <div>
            <Label>Default platforms <span className="text-rose-500">*</span></Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {PLATFORMS.map((p) => {
                const active = form.default_platforms.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlatform(p)}
                    className={`p-1.5 rounded text-xs border ${active ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-zinc-200 text-zinc-600'}`}
                    data-testid={`template-platform-${p.toLowerCase().replace(/[^a-z]/g, '')}`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Default run length (weeks)</Label>
              <Input
                type="number"
                min={1}
                max={MAX_WEEKS}
                value={form.default_weeks}
                onChange={(e) => setForm({ ...form, default_weeks: Math.max(1, Math.min(MAX_WEEKS, Number(e.target.value) || 1)) })}
                data-testid="template-weeks-input"
              />
              <p className="text-[11px] text-zinc-500">~{form.default_weeks * 7} days. User can override at generation time.</p>
            </div>
            {isAdmin && (
              <div className="space-y-1">
                <Label className="flex items-center gap-1">
                  <Globe2 className="w-3.5 h-3.5 text-indigo-600" />
                  Visibility (admin)
                </Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="template-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Just for me</SelectItem>
                    <SelectItem value="global">Global — all users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* 7-day schedule */}
          <div className="space-y-2 border-t border-zinc-200 pt-3">
            <Label className="text-sm font-semibold">Weekly pattern</Label>
            <p className="text-[11px] text-zinc-500 -mt-1">
              Each day's topic. This pattern is what users see when they apply this template.
            </p>
            <div className="space-y-1.5">
              {DAYS.map((d) => {
                const day = form.schedule[d];
                return (
                  <div key={d} className="bg-zinc-50 rounded p-2 space-y-1.5 sm:space-y-0 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center">
                    <div className="flex items-center gap-2 sm:contents">
                      <div className="sm:col-span-1 font-mono text-xs text-zinc-600 font-semibold w-8 shrink-0">{DAY_SHORT[d]}</div>
                      <Input
                        className="flex-1 sm:col-span-6 h-8 text-sm"
                        placeholder="Topic for this day (required)"
                        value={day.topic}
                        onChange={(e) => setForm({
                          ...form,
                          schedule: { ...form.schedule, [d]: { ...day, topic: e.target.value } },
                        })}
                        data-testid={`tpl-day-topic-${d}`}
                      />
                    </div>
                    <div className="grid grid-cols-5 gap-2 sm:contents">
                      <Select
                        value={day.tone || '__default__'}
                        onValueChange={(v) => setForm({
                          ...form,
                          schedule: { ...form.schedule, [d]: { ...day, tone: v === '__default__' ? '' : v } },
                        })}
                      >
                        <SelectTrigger className="col-span-3 sm:col-span-3 h-8 text-xs" data-testid={`tpl-day-tone-${d}`}>
                          <SelectValue placeholder="Tone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__default__">↳ Default ({form.default_tone})</SelectItem>
                          {TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input
                        type="time"
                        className="col-span-2 sm:col-span-2 h-8 text-xs"
                        value={day.time || '09:00'}
                        onChange={(e) => setForm({
                          ...form,
                          schedule: { ...form.schedule, [d]: { ...day, time: e.target.value } },
                        })}
                        data-testid={`tpl-day-time-${d}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="template-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bookmark className="w-4 h-4 mr-2" />}
            {isNew ? 'Create template' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WeeklyThemesPage;
